import React, { useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp, X, Check } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * AiSuggestionPanel – shared UI component for all three AI assistants.
 * Renders a collapsible, dismissible panel with up to 3 suggestions.
 *
 * Props:
 *  - title: string (e.g. "AI Review Summary", "AI Suggestion")
 *  - suggestions: Array<{ id, text, detail?, action?, actionLabel? }>
 *  - loading: boolean
 *  - onDismiss: () => void
 *  - onAccept: (suggestion) => void
 *  - className: string (extra classes)
 */
export default function AiSuggestionPanel({
  title = 'AI Suggestions',
  suggestions = [],
  loading = false,
  onDismiss,
  onAccept,
  className = '',
}) {
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [acceptedIds, setAcceptedIds] = useState(new Set());

  if (dismissed || (!loading && suggestions.length === 0)) return null;

  const visibleSuggestions = suggestions.slice(0, 3);

  const handleAccept = (s) => {
    setAcceptedIds(prev => new Set(prev).add(s.id));
    onAccept?.(s);
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div
      className={`border border-zinc-200 rounded-xl bg-white overflow-hidden transition-all ${className}`}
      data-testid="ai-suggestion-panel"
    >
      {/* Header bar */}
      <div
        className="w-full flex items-center justify-between px-4 py-2.5 bg-zinc-50 hover:bg-zinc-100 transition-colors cursor-pointer"
        data-testid="ai-panel-toggle"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span className="text-xs font-bold text-zinc-700 uppercase tracking-wider">{title}</span>
          {!expanded && suggestions.length > 0 && (
            <span className="text-[10px] text-zinc-400 bg-zinc-200 rounded-full px-1.5 py-0.5">{suggestions.length}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            className="p-0.5 rounded hover:bg-zinc-200 text-zinc-400 hover:text-zinc-600"
            data-testid="ai-panel-dismiss"
            type="button"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="px-4 py-3 space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 py-2" data-testid="ai-panel-loading">
              <div className="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-zinc-500">Analyzing...</span>
            </div>
          ) : (
            visibleSuggestions.map((s) => {
              const isAccepted = acceptedIds.has(s.id);
              return (
                <div
                  key={s.id}
                  className={`flex items-start gap-3 p-2.5 rounded-lg transition-colors ${
                    isAccepted ? 'bg-emerald-50 border border-emerald-200' : 'bg-zinc-50'
                  }`}
                  data-testid={`ai-suggestion-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-800">{s.text}</p>
                    {s.detail && <p className="text-xs text-zinc-500 mt-0.5">{s.detail}</p>}
                  </div>
                  {s.action && !isAccepted && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs flex-shrink-0 border-amber-200 text-amber-700 hover:bg-amber-50"
                      onClick={() => handleAccept(s)}
                      data-testid={`ai-accept-${s.id}`}
                    >
                      {s.actionLabel || 'Accept'}
                    </Button>
                  )}
                  {isAccepted && (
                    <span className="flex items-center gap-1 text-xs text-emerald-600 flex-shrink-0">
                      <Check className="w-3.5 h-3.5" /> Applied
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
